<template>
    <div class="main-container pa-5">
        <div class="top-row">
            <h4 class="mb-2">{{ $t('info_cat_desc') }}</h4>
        </div>
        <v-form ref="form" :disabled="!record.userCanSave(authUser)" @input="$emit('input', $event)">
            <v-row>
                <!-- linke Spalte -->
                <v-col cols="12" lg="6">
                    <v-row dense>
                        <v-col cols="12">
                            <LabelField :label="$t('title')">
                                <KpTextField
                                    :value="record.title"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    clearable
                                    :counter="globalParam('event.max_title_chars', 70)"
                                    :rules="[
                                        getRule('required'),
                                        getRule('maxLength')(globalParam('event.max_title_chars', 70)),
                                    ]"
                                    :disabled="!record.canEditTitle"
                                    :max-length="globalParam('event.max_title_chars', 70)"
                                    @input="onInput('title', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="6">
                            <LabelField :label="$t('event_organizer')">
                                <EventOrganizerSelect
                                    :event="record"
                                    :required="eoMandatory"
                                    @selected="onEventOrganizerSelect($event)"
                                />
                            </LabelField>
                        </v-col>
                        <v-col cols="6">
                            <LabelField :label="$t('event_location')" :label-tip="$t('event_location_help')">
                                <LocationSelectField
                                    :location-id="record.location_id"
                                    :organizer-id="record.organizer_id"
                                    :placeholder="$t('placeholder_event_info_categories_location_id')"
                                    @selected="onEventLocationSelect($event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" lg="6">
                            <LabelField :label="$t('hauptsparten')">
                                <GenreGroupSelect
                                    :value="record.genre_groups"
                                    :page-id="1"
                                    :placeholder="$t('placeholder_event_info_categories_genre_groups')"
                                    :rules="getRules('required')"
                                    :disabled="!record.canEditGenreGroups"
                                    clearable
                                    multiple
                                    chips
                                    deletable-chips
                                    small-chips
                                    hide-details="auto"
                                    @input="onInput('genre_groups', $event)"
                                />
                            </LabelField>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <LabelField :label="$t('frontsparte')" :label-tip="$t('frontsparte_tip')">
                                <GenreGroupSelect
                                    :value="record.main_genre_group_id"
                                    :page-id="1"
                                    :placeholder="$t('placeholder_event_info_categories_main_genre_group_id')"
                                    :rules="getRules('required')"
                                    :disabled="!record.canEditMainGenreGroup"
                                    :allow-only="record.genre_groups || []"
                                    clearable
                                    hide-details="auto"
                                    @input="onInput('main_genre_group_id', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <LabelField
                                :label="$t('interessen_tags_erweiterte_suche')"
                                :label-tip="$t('interessen_tags_erweiterte_suche_tip')"
                            >
                                <TagSelect
                                    :value="record.tags"
                                    item-value="id"
                                    :placeholder="$t('placeholder_event_description_tags')"
                                    show-add
                                    show-download
                                    for-event
                                    clearable
                                    hide-details="auto"
                                    @input="onInput('tags', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" lg="6">
                            <LabelField :label="$t('hauptzielgruppe')">
                                <TargetAudienceSelect
                                    :value="record.target_audience_id"
                                    :placeholder="$t('placeholder_event_additional_info_target_audience_id')"
                                    :disabled="!record.canEditTargetAudience"
                                    clearable
                                    hide-details="auto"
                                    @input="onInput('target_audience_id', $event)"
                                />
                            </LabelField>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <LabelField :label="$t('altersbereich')">
                                <KpTextArea
                                    :value="record.typical_age_range"
                                    rows="4"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :placeholder="$t('placeholder_typical_age_range')"
                                    @input="onInput('typical_age_range', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col>
                            <LabelField
                                :label="$t('original_event_url')"
                                :label-tip="$t('original_event_url_tip')"
                                class="mt-6"
                            >
                                <UrlField
                                    :value="record.original_event_url"
                                    :placeholder="$t('placeholder_url')"
                                    :rules="[getRule('maxLength')(1000)]"
                                    @input="onInput('original_event_url', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col>
                            <LabelField
                                :label="$t('weiterfuehrende_links')"
                                :label-tip="$t('weiterfuehrende_links_label_tip')"
                            >
                                <MultiItemContainer
                                    :value="record.external_links"
                                    text-name="description"
                                    item-name="url"
                                    :show-type-field="false"
                                    item-class="mb-2"
                                    @input="onInput('external_links', $event)"
                                    @change="onInput('external_links', $event)"
                                >
                                    <template #text="slotProps">
                                        <KpTextField
                                            :value="slotProps.value"
                                            :placeholder="$t('placeholder_event_additional_info_external_links_text')"
                                            dense
                                            outlined
                                            hide-details="auto"
                                            clearable
                                            @input="slotProps.input"
                                        />
                                    </template>
                                    <template #default="slotProps">
                                        <UrlField
                                            :value="slotProps.value"
                                            :placeholder="$t('placeholder_event_additional_info_external_links_item')"
                                            @input="slotProps.input"
                                        />
                                    </template>
                                </MultiItemContainer>
                            </LabelField>
                        </v-col>
                    </v-row>
                </v-col>

                <!-- rechte Spalte -->
                <v-col cols="12" lg="6">
                    <div class="d-flex" :class="shortDescriptionOrder">
                        <LabelField :label="$t('description_short')" :label-tip="$t('description_short_tip')">
                            <KpTextArea
                                :value="record.summary_text"
                                dense
                                rows="5"
                                outlined
                                hide-details="auto"
                                counter="200"
                                :placeholder="$t('event_description_short_placeholder')"
                                :rules="[getRule('eventSummaryText')]"
                                :max-length="200"
                                @input="onInput('summary_text', $event)"
                            />
                        </LabelField>
                        <LabelField
                            v-if="showAbstract"
                            :label="$t('description_short_2')"
                            :label-tip="$t('event_description_short_2_placeholder')"
                        >
                            <KpTextArea
                                :value="record.abstract"
                                :placeholder="$t('event_description_short_2_placeholder')"
                                dense
                                rows="3"
                                outlined
                                hide-details="auto"
                                counter="60"
                                :rules="[getRule('eventAbstractText')]"
                                :disabled="!record.canEditAbstract"
                                :max-length="60"
                                @input="onInput('abstract', $event)"
                            />
                        </LabelField>
                    </div>
                    <v-row dense>
                        <v-col cols="12">
                            <LabelField :label="$t('description')">
                                <TinymceEditor
                                    :value="record.description"
                                    :init="tmceConfig"
                                    :placeholder="$t('event_description_placeholder')"
                                    @input="onInput('description', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { userIsStaff } from '@/helpers/auth';
import GlobalsMixin from '@/mixins/globals';
import getRulesMixin from '@/mixins/get-form-rules';

import { Event } from '@/models/Event';
import { EventOrganizerCollection } from '@/models/EventOrganizer';
import MultiItemContainer from '@/components/multi_item/Container.vue';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import TagSelect from '@/components/tag/select.vue';
import LocationSelectField from '@/components/location/SelectField.vue';
import EventOrganizerSelect from '@/components/event/EventOrganizerSelect.vue';
import { fieldRuleValue } from '@/helpers/tools';
import TargetAudienceSelect from '@/components/target_audience/select.vue';
import KpTextArea from '@/components/KpTextArea.vue';
import KpTextField from '@/components/KpTextField.vue';

export default {
    name: 'EventExpressEditInfo',
    components: {
        MultiItemContainer,
        GenreGroupSelect,
        TagSelect,
        LocationSelectField,
        EventOrganizerSelect,
        TargetAudienceSelect,
        KpTextArea,
        KpTextField,
    },
    mixins: [GlobalsMixin, getRulesMixin],
    props: {
        record: {
            type: Event,
            required: true,
        },
        // used to indicate form validity
        value: Boolean,
    },
    data() {
        const eventOrganizerCollection = new EventOrganizerCollection();
        const tinyMCEConfig = userIsStaff()
            ? {
                  // Config for staff users:
                  toolbar1:
                      'link unlink | blocks | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist | paste',
                  toolbar2: 'template | image media | code | fullscreen | undo redo',
                  body_id: 'agendaPage_get',
                  body_class: 'agenda mce-editor-body',
                  max_height: null,
              }
            : {
                  // Config for non-staff (veranstalter) users:
                  toolbar1: 'link unlink | bold italic | paste |code | fullscreen | undo redo',
                  toolbar2: null,
                  body_id: 'agendaPage_get',
                  body_class: 'agenda mce-editor-body',
                  paste_data_images: false,
                  paste_block_drop: false,
                  max_height: null,
              };
        return {
            isValid: false,
            eventOrganizerCollection,
            tmceConfig: tinyMCEConfig,
        };
    },
    computed: {
        ...mapState('user', {
            authUser: 'record',
        }),
        eoMandatory() {
            return fieldRuleValue('field_rules.mandatory.event.organizer_id', false);
        },
        shortDescriptionOrder() {
            return this.globalParam('event.express.reverse_short_description_order')
                ? 'flex-column-reverse'
                : 'flex-column';
        },
        showAbstract() {
            if (this.globalParam('event.show_abstract_for_everyone')) {
                return true;
            }
            if (userIsStaff()) {
                return true;
            }
            return false;
        },
    },
    mounted() {
        this.validateForm();
    },
    methods: {
        userIsStaff,

        onInput(field, value) {
            this.record.set(field, value);

            // special handling for genre group fields 'genre_groups' and 'main_genre_group_id:'
            // The main genre group should be set to the first selected genre group:
            if (!this.record.main_genre_group_id && this.record.genre_groups?.length > 0) {
                this.record.set('main_genre_group_id', this.record.genre_groups[0]);
            }
            // main genre group can only be one of the
            // selected genre groups in the "Hauptsparten" (genre_groups) Field:
            if (!this.record.genre_groups?.includes(this.record.main_genre_group_id)) {
                this.record.set('main_genre_group_id', null);
            }
        },
        validateForm() {
            this.$refs.form.validate();
        },

        onSave() {
            this.$emit('save');
        },

        onEventOrganizerSelect(record) {
            // Nach dem event-organizer-Select setzen wir sowohl den Display-Wert (damit man gleich sieht
            // was gewählt wurde), sowie die ID:
            if (record) {
                this.onInput('organizer_id', record.id);
                this.onInput('event_organizer', record.displayTitle);
            } else {
                this.onInput('organizer_id', null);
                this.onInput('event_organizer', null);
            }
        },

        onEventLocationSelect(record) {
            if (record) {
                this.onInput('location_id', record.id);
                // copy accessibility from location to event
                // Not anymore, see ticket #24226:
                // this.onInput('accessibility', record.accessibility);
                // this.onInput('accessibility_link', record.accessibility_link);
            } else {
                this.onInput('location_id', null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    overflow: auto;
    width: 100%;
}
</style>
